import { IFilterOptionDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { upperFirst } from 'lodash';

import { AgGridFilterOption } from 'core/enums';

export const dateFilterComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: unknown
): number => {
  if (cellValue == null || cellValue === '') {
    return -1;
  }

  const cellDate = dayjs(cellValue as string | Date);

  if (!cellDate.isValid()) {
    return -1;
  }

  const cellMidnight = cellDate.startOf('day').valueOf();
  const filterMidnight = dayjs(filterLocalDateAtMidnight).startOf('day').valueOf();

  if (cellMidnight === filterMidnight) {
    return 0;
  }

  return cellMidnight < filterMidnight ? -1 : 1;
};

export const isRelativeDateFilterOption = (option: AgGridFilterOption) => {
  return [
    AgGridFilterOption.RELATIVE_TODAY,
    AgGridFilterOption.RELATIVE_YESTERDAY,
    AgGridFilterOption.RELATIVE_LAST_7_DAYS,
    AgGridFilterOption.RELATIVE_LAST_30_DAYS,
    AgGridFilterOption.RELATIVE_LAST_90_DAYS,
  ].includes(option);
};

export const getCustomDateFilter = (): IFilterOptionDef[] => {
  return [
    {
      displayKey: AgGridFilterOption.RELATIVE_TODAY,
      displayName: t(`label${upperFirst(AgGridFilterOption.RELATIVE_TODAY)}`),
      numberOfInputs: 0,
      predicate: (_, cellValue) =>
        isDateMatchingRelativeFilter(cellValue, AgGridFilterOption.RELATIVE_TODAY),
    },
    {
      displayKey: AgGridFilterOption.RELATIVE_YESTERDAY,
      displayName: t(`label${upperFirst(AgGridFilterOption.RELATIVE_YESTERDAY)}`),
      numberOfInputs: 0,
      predicate: (_, cellValue) =>
        isDateMatchingRelativeFilter(cellValue, AgGridFilterOption.RELATIVE_YESTERDAY),
    },
    {
      displayKey: AgGridFilterOption.RELATIVE_LAST_7_DAYS,
      displayName: t(`label${upperFirst(AgGridFilterOption.RELATIVE_LAST_7_DAYS)}`),
      numberOfInputs: 0,
      predicate: (_, cellValue) =>
        isDateMatchingRelativeFilter(cellValue, AgGridFilterOption.RELATIVE_LAST_7_DAYS),
    },
    {
      displayKey: AgGridFilterOption.RELATIVE_LAST_30_DAYS,
      displayName: t(`label${upperFirst(AgGridFilterOption.RELATIVE_LAST_30_DAYS)}`),
      numberOfInputs: 0,
      predicate: (_, cellValue) =>
        isDateMatchingRelativeFilter(cellValue, AgGridFilterOption.RELATIVE_LAST_30_DAYS),
    },
    {
      displayKey: AgGridFilterOption.RELATIVE_LAST_90_DAYS,
      displayName: t(`label${upperFirst(AgGridFilterOption.RELATIVE_LAST_90_DAYS)}`),
      numberOfInputs: 0,
      predicate: (_, cellValue) =>
        isDateMatchingRelativeFilter(cellValue, AgGridFilterOption.RELATIVE_LAST_90_DAYS),
    },
  ];
};

export const getDateRangeForRelativeFilter = (
  type: AgGridFilterOption
): { from: Date | null; to: Date | null } => {
  const today = dayjs().startOf('day');

  switch (type) {
    case AgGridFilterOption.RELATIVE_TODAY:
      return { from: today.toDate(), to: today.endOf('day').toDate() };
    case AgGridFilterOption.RELATIVE_YESTERDAY:
      return {
        from: today.subtract(1, 'day').toDate(),
        to: today.subtract(1, 'day').endOf('day').toDate(),
      };
    case AgGridFilterOption.RELATIVE_LAST_7_DAYS:
      return { from: today.subtract(6, 'day').toDate(), to: today.endOf('day').toDate() };
    case AgGridFilterOption.RELATIVE_LAST_30_DAYS:
      return { from: today.subtract(29, 'day').toDate(), to: today.endOf('day').toDate() };
    case AgGridFilterOption.RELATIVE_LAST_90_DAYS:
      return { from: today.subtract(89, 'day').toDate(), to: today.endOf('day').toDate() };
    default:
      return { from: null, to: null };
  }
};

const isDateMatchingRelativeFilter = (value: unknown, type: AgGridFilterOption): boolean => {
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) {
    return false;
  }

  const cellDate = dayjs(value).startOf('day');
  const { from, to } = getDateRangeForRelativeFilter(type);

  if (!from || !to) {
    return false;
  }

  const fromDate = dayjs(from);
  const toDate = dayjs(to);

  // INCLUSIVE comparison: cellDate >= fromDate && cellDate <= toDate
  return (
    (cellDate.isSame(fromDate) || cellDate.isAfter(fromDate)) &&
    (cellDate.isSame(toDate) || cellDate.isBefore(toDate))
  );
};
